import React, { useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Anchor, Box, Text, useResponsive } from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';
import VerifyMyCardModal from '@/modules/core/components/Dashboard/components/BannersList/CompleteBanner/components/VerifyMyCardModal';
import SuccessActivatedModal from '@/modules/core/components/Dashboard/components/BannersList/CompleteBanner/components/VerifyMyCardModal/components/SuccessActivatedModal';
import { CardActivationVariants } from '@/modules/core/components/Dashboard/components/BannersList/CompleteBanner/components/VerifyMyCardModal/VerifyMyCardModal.constants';
import VerifyEmployeeButton from '@/modules/core/components/Dashboard/components/BannersList/CompleteBanner/VerifyEmployeeButton';

import { useHandleRefetchCards } from './LimitedAccessVerification/hooks';
import { useShowLimitedAccessTopBanner } from './hooks';
import LimitedAccessInfoModal from './LimitedAccessInfoModal';
import LimitedAccessVerification from './LimitedAccessVerification';

interface Props {}

const LimitedAccessBanner: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [isVerificationModalVisible, setVerificationModalVisible] = useState(false);
  const [isVerificationCardVisible, setVerificationCardVisible] = useState(false);
  const [isSuccessCardModalVisible, setSuccessCardModalVisible] = useState(false);
  const [isSuccessEmailModalVisible, setSuccessEmailModalVisible] = useState(false);
  const [infoModalVisible, setInfoModalVisible] = useState(true);
  const [isActivationSuccess, setIsActivationSuccess] = useState(CardActivationVariants.ACTIVATION_NOT_INIT);

  const handleToggle = useCallback(() => {
    setVerificationModalVisible(false);
    setInfoModalVisible(!infoModalVisible);
  }, [infoModalVisible]);
  const handleRefetch = useHandleRefetchCards();
  const topNode = document.getElementById(`top-bar-portal`);
  const handleOpenModal = () => {
    setVerificationModalVisible(true);
    setInfoModalVisible(false);
  };
  const handleCloseSuccess = () => {
    handleRefetch();
    setSuccessCardModalVisible(false);
    setSuccessEmailModalVisible(false);
    setVerificationModalVisible(false);
    setVerificationCardVisible(false);
  };
  const isLimitedAccessTopBanner = useShowLimitedAccessTopBanner();
  if (!isLimitedAccessTopBanner) {
    return null;
  }

  return (
    <>
      {isSuccessCardModalVisible && (
        <SuccessActivatedModal
          onSetVisible={handleCloseSuccess}
          isActivationSuccess={CardActivationVariants.ACTIVATED}
        />
      )}
      {isSuccessEmailModalVisible && (
        <SuccessActivatedModal
          isEmailMethod
          onSetVisible={handleCloseSuccess}
          isActivationSuccess={isActivationSuccess}
        />
      )}
      {isVerificationCardVisible && <VerifyMyCardModal onSetVisible={setVerificationCardVisible} />}
      {topNode &&
        ReactDOM.createPortal(
          <Box margin={{ right: 'spacing24' }}>
            <VerifyEmployeeButton width="auto" size="M" onClick={handleOpenModal} />
          </Box>,
          topNode
        )}

      {isVerificationModalVisible && (
        <LimitedAccessVerification
          onSuccess={() => setSuccessEmailModalVisible(true)}
          visible={isVerificationModalVisible}
          onSetVisible={setVerificationModalVisible}
          onOpenCardVerification={() => setVerificationCardVisible(true)}
          setIsActivationSuccess={setIsActivationSuccess}
        />
      )}
      {isVerificationCardVisible && (
        <VerifyMyCardModal
          onSetVisible={setVerificationCardVisible}
          onSuccess={() => setSuccessCardModalVisible(true)}
          refreshCardList={handleRefetch}
        />
      )}

      <LimitedAccessInfoModal
        isVisible={infoModalVisible}
        onSetVisible={setInfoModalVisible}
        buttonNode={
          <VerifyEmployeeButton testId="verify-my-card_modal" size="L" fill onClick={handleOpenModal}>
            {t('Verify My Account')}
          </VerifyEmployeeButton>
        }
      />
      <Box
        data-testid="account-access-limited-banner"
        width="100%"
        height={isMobile ? undefined : '62px'}
        direction={isMobile ? 'column' : 'row'}
        background="warningBorder"
        justify="center"
        align="center"
        pad={isMobile ? { top: 'spacing24', bottom: 'spacing16' } : { horizontal: 'spacing16' }}
        style={{
          position: 'fixed',
          zIndex: 1003,
          top: 0,
        }}
      >
        {isMobile ? (
          <Box direction="column" align="center" justify="center">
            <Text size="large" data-testid="account-access-limited-banner_text" weight={700}>
              Account access limited:
            </Text>
            <Box direction="row" align="center">
              <Text size="large">Verify your identity now.</Text>
              <Anchor
                target="_blank"
                data-testId="limited-access_learn-more_button"
                onClick={handleToggle}
                margin={{ left: 'spacing4' }}
              >
                <Text size="large" color="textAccent" weight={700}>
                  {t('Learn More')}
                </Text>
              </Anchor>
            </Box>
          </Box>
        ) : (
          <>
            <Text data-testid="account-access-limited-banner_text">
              Account access limited: Verify your identity now.
            </Text>
            <Box>
              <AppButton
                buttonType="quaternary"
                testId="limited-access_learn-more"
                onClick={handleToggle}
                style={{ padding: '0px 10px' }}
              >
                <Text size="medium" weight={700}>
                  {t('Learn More')}
                </Text>
              </AppButton>
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default LimitedAccessBanner;
